import React, { useCallback, useEffect, useMemo, useState, useRef, useId, Fragment, Suspense } from 'react';
import { motion } from 'framer-motion';
/**
 * ReferenceArrow — V3 (TAA audit §6 / §11.8 / S5)
 *
 * Renders a single sentence (or two-sentence passage) with a curved arrow
 * drawn from a pronoun back to its referent. Used as a `stimulusAid`
 * (when the diagram IS the prompt) or as a `supportAid` (revealed after a
 * wrong answer to make the verwijzing zichtbaar).
 *
 * Implementation note: words are rendered as inline-block spans so we can
 * read their bounding boxes after layout and draw an SVG path between the
 * centers of the two highlighted words. We use a ResizeObserver-free
 * approach by measuring on mount + window resize.
 */
interface ReferenceArrowProps {
  /** Full passage as plain text. Words are split on whitespace. */
  sentence: string;
  /** 0-based index of the pronoun word (the verwijswoord, e.g. "daar"). */
  pronounIndex: number;
  /** 0-based index of the referent word (what the pronoun points to). */
  referentIndex: number;
  /** Optional caption shown above the diagram. */
  label?: string;
}
export function ReferenceArrow({
  sentence,
  pronounIndex,
  referentIndex,
  label
}: ReferenceArrowProps) {
  // Guard: a pronoun can never refer to itself. Render the sentence
  // without an arrow rather than producing a zero-length SVG path.
  const isSelfReference = pronounIndex === referentIndex;
  const containerRef = useRef<HTMLDivElement>(null);
  const pronounRef = useRef<HTMLSpanElement>(null);
  const referentRef = useRef<HTMLSpanElement>(null);
  const [arrow, setArrow] = useState<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  } | null>(null);
  const arrowId = useId();
  const words = useMemo(() => sentence.split(/(\s+)/), [sentence]);
  // Filter to only "real" words (non-whitespace) for index mapping
  const wordPositions = useMemo(() => {
    const positions: number[] = [];
    let wordIdx = 0;
    words.forEach((token, i) => {
      if (token.trim().length > 0) {
        positions[wordIdx] = i;
        wordIdx++;
      }
    });
    return positions;
  }, [words]);
  const measure = useCallback(() => {
    const container = containerRef.current;
    const pronoun = pronounRef.current;
    const referent = referentRef.current;
    if (!container || !pronoun || !referent) return false;
    const cBox = container.getBoundingClientRect();
    const pBox = pronoun.getBoundingClientRect();
    const rBox = referent.getBoundingClientRect();
    setArrow({
      x1: pBox.left + pBox.width / 2 - cBox.left,
      y1: pBox.top - cBox.top,
      x2: rBox.left + rBox.width / 2 - cBox.left,
      y2: rBox.top - cBox.top
    });
    return true;
  }, []);
  useEffect(() => {
    if (isSelfReference) return;
    // rAF retry: refs may still be null on first paint when this component
    // mounts inside Suspense or AnimatePresence. Try up to 3 frames.
    let frameId = 0;
    let attempts = 0;
    const tryMeasure = () => {
      if (measure()) return;
      if (attempts++ < 3) {
        frameId = window.requestAnimationFrame(tryMeasure);
      }
    };
    tryMeasure();
    // Throttle resize via rAF — orientation change can fire 60+ events.
    let resizeFrame = 0;
    const onResize = () => {
      if (resizeFrame) return;
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = 0;
        measure();
      });
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (frameId) window.cancelAnimationFrame(frameId);
      if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
    };
  }, [measure, sentence, pronounIndex, referentIndex, isSelfReference]);
  // Build curved-path d-attribute: arc from pronoun upwards to referent
  const path = useMemo(() => {
    if (!arrow) return '';
    const {
      x1,
      y1,
      x2,
      y2
    } = arrow;
    // If words are on different lines (y difference is large), draw a more direct path or adjust arc
    const yDiff = Math.abs(y1 - y2);
    const isMultiline = yDiff > 20;
    const arcHeight = Math.min(Math.max(28, Math.abs(x1 - x2) * 0.25), 80);
    if (isMultiline) {
      // For multiline, a simple quadratic bezier might look weird if it loops too high
      // We use a cubic bezier to make an S-curve or a gentler arc
      const cy1 = y1 - arcHeight;
      const cy2 = y2 - arcHeight;
      return `M ${x2} ${y2} C ${x2} ${cy2}, ${x1} ${cy1}, ${x1} ${y1}`;
    }
    const cy = Math.min(y1, y2) - arcHeight;
    return `M ${x2} ${y2} Q ${(x1 + x2) / 2} ${cy} ${x1} ${y1}`;
  }, [arrow]);
  return <figure className="flex flex-col items-center gap-2 rounded-xl border border-slate-200 bg-white p-4" aria-label={`Verwijzing in zin: ${sentence}`} data-id="element-12827">
      {label && <figcaption className="text-xs font-medium uppercase tracking-wide text-slate-500" data-id="element-12828">
          {label}
        </figcaption>}

      <div ref={containerRef} className="relative w-full pt-12 pb-2 px-2 text-base md:text-lg leading-loose text-slate-800" data-id="element-12829">
        {!isSelfReference && arrow && path && <svg className="pointer-events-none absolute inset-0 h-full w-full overflow-visible" aria-hidden="true" data-id="element-12830">
            <defs data-id="element-12831">
              <marker id={`arrowhead-${arrowId}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse" data-id="element-12832">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#6366f1" data-id="element-12833" />
              </marker>
            </defs>
            <motion.path d={path} fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" markerEnd={`url(#arrowhead-${arrowId})`} initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} data-id="element-12834" />
          </svg>}

        <p className="m-0" data-id="element-12835">
          {words.map((token, i) => {
          if (token.trim().length === 0) {
            return <Fragment key={i} data-id="element-12836">{token}</Fragment>;
          }
          const realWordIdx = wordPositions.indexOf(i);
          const isPronoun = realWordIdx === pronounIndex;
          const isReferent = realWordIdx === referentIndex;
          if (isPronoun) {
            return <span key={i} ref={pronounRef} className="inline-block rounded bg-indigo-100 px-1.5 py-0.5 font-semibold text-indigo-800 border border-indigo-300" data-id="element-12837">
                  {token}
                </span>;
          }
          if (isReferent) {
            return <span key={i} ref={referentRef} className="inline-block rounded bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-800 border border-emerald-300" data-id="element-12838">
                  {token}
                </span>;
          }
          return <span key={i} data-id="element-12839">{token}</span>;
        })}
        </p>
      </div>

      <div className="flex items-center gap-3 text-[11px] text-slate-500" data-id="element-12840">
        <span className="flex items-center gap-1" data-id="element-12841">
          <span className="inline-block h-2 w-2 rounded-full bg-indigo-400" data-id="element-12842" />
          verwijswoord
        </span>
        <span className="flex items-center gap-1" data-id="element-12843">
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-400" data-id="element-12844" />
          waar het naar verwijst
        </span>
      </div>
    </figure>;
}
export default ReferenceArrow;